import Axios from 'axios';
import { saveShopData, getShopData } from '../utilities/shopStorage';
import { pushDatalayer } from '../utilities/datalayers';

/* Config */
const accessKey = window.shopwareConfig?.accessKey;

function formatProductForDatalayer(product) {
    const categories = product.categories || [];
    const categoryNames = categories.map(cat => cat.translated?.name || '');

    return {
        item_name: product?.translated?.name || '',
        item_id: product?.id || '',
        price: product?.calculatedPrice?.unitPrice?.toFixed(2) || '0.00',
        item_brand: product?.manufacturer?.translated?.name || '',
        item_category: categoryNames[0] || '',
        item_variant: product?.productNumber || '',
        item_list_name: 'Productdetail',
        item_list_id: 'product-detail',
        index: 1,
        quantity: '1'
    };
}

async function createNewCart({shopwareUrl}) {
    const url = `${shopwareUrl}/checkout/cart`;

    try {
        const response = await Axios.post(url, {}, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
            }
        });

        const newToken = response.headers['sw-context-token'];

        if (newToken) {
            saveShopData('sw-context-token', newToken);
        }

        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function ensureValidContextToken({shopwareUrl}) {
    const existingToken = getShopData('sw-context-token');

    if (!existingToken) {
        await createNewCart({shopwareUrl});
    }

    return getShopData('sw-context-token');
}

async function handleCartError(error, shopwareUrl) {
    if (error.response?.status === 403 || error.response?.status === 401 || error.response?.status === 404) {
        try {
            await createNewCart({shopwareUrl});
            return true;
        } catch (createError) {
            return false;
        }
    }
    return false;
}

export async function fetchOrCreateCart({shopwareUrl}) {
    const url = `${shopwareUrl}/checkout/cart`;

    try {
        const response = await Axios.get(url, {
            headers: {
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-context-token': getShopData('sw-context-token') || ''
            }
        });
        return response.data;
    } catch (error) {
        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            const response = await Axios.get(url, {
                headers: {
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-context-token': getShopData('sw-context-token') || ''
                }
            });
            return response.data;
        }
        throw error;
    }
}

export async function getCartItems({shopwareUrl}) {
    try {
        return await fetchOrCreateCart({shopwareUrl});
    } catch (error) {
        throw error;
    }
}

export async function emailCart(emailAddress, cartId, licensePlate, products) {
    const url = '/api/cart/email';

    try {
        const response = await Axios.post(url, {
            email: emailAddress,
            cartId: cartId,
            licensePlate: licensePlate
        }, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            }
        });

        return response.data;
    } catch (error) {
        throw error;
    }
}

export async function isProductInCart({productId, shopwareUrl}) {
    try {
        const cartData = await fetchOrCreateCart({shopwareUrl});

        if (!cartData.lineItems || !Array.isArray(cartData.lineItems)) {
            return false;
        }

        // Check if any line item has the same referencedId (product ID)
        return cartData.lineItems.some(item =>
            item.referencedId === productId || item.id === productId
        );
    } catch (error) {
        return false;
    }
}

export async function addToCart({id, shopwareUrl, allowDuplicates = false, productData = null}) {
    // Check for duplicates unless explicitly allowed
    if (!allowDuplicates) {
        const productExists = await isProductInCart({productId: id, shopwareUrl});
        if (productExists) {
            const error = new Error('Product already exists in cart');
            error.name = 'DuplicateProductError';
            error.productId = id;
            throw error;
        }
    }

    await ensureValidContextToken({shopwareUrl});

    const url = `${shopwareUrl}/checkout/cart/line-item`;

    try {
        const response = await Axios.post(url,
            {
                items: [
                    {
                        id: id,
                        type: "product",
                        referencedId: id,
                        quantity: 1,
                    }
                ]
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    'sw-context-token': getShopData('sw-context-token') || ''
                }
            }
        );

        if (response.data) {
            const { elements } = response.data;

            if (productData) {
                const datalayerData = {
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: 'EUR',
                        items: [formatProductForDatalayer(productData)]
                    }
                };
                pushDatalayer(datalayerData);
            }

            window.dispatchEvent(new CustomEvent('cart:updated'));

            return elements;
        } else {
            throw new Error('No data found');
        }

    } catch (error) {
        if (error.name === 'DuplicateProductError') {
            throw error;
        }

        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            const retryResponse = await Axios.post(url,
                {
                    items: [
                        {
                            id: id,
                            type: "product",
                            referencedId: id,
                            quantity: 1,
                            stackable: false
                        }
                    ]
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'sw-access-key': accessKey,
                        'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                        'sw-context-token': getShopData('sw-context-token') || ''
                    }
                }
            );

            if (retryResponse.data) {
                const { elements } = retryResponse.data;

                if (productData) {
                    const datalayerData = {
                        event: 'add_to_cart',
                        ecommerce: {
                            currency: 'EUR',
                            items: [formatProductForDatalayer(productData)]
                        }
                    };
                    pushDatalayer(datalayerData);
                }

                window.dispatchEvent(new CustomEvent('cart:updated'));
                return elements;
            }
        }
        throw error;
    }
}

export async function removeFromCart(id, shopwareUrl, productData = null) {

    await ensureValidContextToken({shopwareUrl});

    const url = `${shopwareUrl}/checkout/cart/line-item/delete`;

    try {
        const response = await Axios.post(url, {
            ids: [id]
        }, {
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'sw-access-key': accessKey,
                'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                'sw-context-token': getShopData('sw-context-token')
            }
        });

        if (response.data) {
            if (productData) {
                const datalayerData = {
                    event: 'remove_from_cart',
                    ecommerce: {
                        currency: 'EUR',
                        items: [formatProductForDatalayer(productData)]
                    }
                };
                pushDatalayer(datalayerData);
            }

            window.dispatchEvent(new CustomEvent('cart:updated'));

            return response.data;
        } else {
            throw new Error('No data found after removal');
        }

    } catch (error) {
        const canRetry = await handleCartError(error, shopwareUrl);
        if (canRetry) {
            const retryResponse = await Axios.post(url, {
                ids: [id]
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'sw-access-key': accessKey,
                    'sw-language-id': '2fbb5fe2e29a4d70aa5854ce7ce3e20b',
                    'sw-context-token': getShopData('sw-context-token')
                }
            });

            if (retryResponse.data) {
                if (productData) {
                    const datalayerData = {
                        event: 'remove_from_cart',
                        ecommerce: {
                            currency: 'EUR',
                            items: [formatProductForDatalayer(productData)]
                        }
                    };
                    pushDatalayer(datalayerData);
                }

                window.dispatchEvent(new CustomEvent('cart:updated'));
                return retryResponse.data;
            }
        }
        throw error;
    }
}
